import { useCallback, useMemo } from 'react';
import { useContentEditorPersonalizationContext } from 'personalization-lib/contexts/ContentEditorPersonalizationContext';
import useCanCreateVariant from 'personalization-lib/hooks/variantCreation/validation/useCanCreateVariant';
import useCreateButtonClickTracker from 'personalization-lib/hooks/tracking/library/useCreateButtonClickTracker';
import { getInitialPanelData } from 'personalization-lib/utils/variantCreation/initialData';
import { getFeatureGates } from 'personalization-lib/utils/auth/featureAccess';
import { createVariantInitialData } from 'personalization-lib/utils/variantCreation';
import { VALIDATION_ERRORS } from 'personalization-lib/constants/validation';
// Internal hook for handling variant creation within the content editor context.
const useContentEditorVariantCreation = ({
  trackingProps
} = {}) => {
  const {
    openPanel: handleOpenPanel,
    assetData,
    isPanelOpen,
    loading,
    error
  } = useContentEditorPersonalizationContext();
  const initialData = useMemo(() => getInitialPanelData(createVariantInitialData(assetData)), [assetData]);
  const trackClick = useCreateButtonClickTracker(Object.assign({}, trackingProps, {
    data: initialData
  }));
  const isVisible = getFeatureGates().createVariantInEditor && (!!assetData || loading);
  const {
    canCreate,
    validationErrors,
    tooltip,
    loading: validationLoading,
    error: validationError
  } = useCanCreateVariant(Object.assign({}, initialData, {
    skip: !isVisible
  }));
  const isDisabled = !canCreate || isPanelOpen || !isVisible || !assetData;
  const isUpgradeRequired = validationErrors.has(VALIDATION_ERRORS.MISSING_PERSONALIZE_EDIT_SCOPE);
  const openPanel = useCallback(() => {
    if (isDisabled) {
      throw new Error('Cannot open panel in invalid state');
    }
    handleOpenPanel();
    trackClick.createPersonalization();
  }, [isDisabled, handleOpenPanel, trackClick]);
  return useMemo(() => ({
    openPanel,
    tooltip,
    loading: loading || validationLoading,
    error: error || validationError,
    isVisible,
    isDisabled,
    isUpgradeRequired
  }), [openPanel, tooltip, loading, validationLoading, error, validationError, isDisabled, isVisible, isUpgradeRequired]);
};
export default useContentEditorVariantCreation;